import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, candidateMenuItems } from '../../components/DashboardLayout';
import { candidatesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, User } from 'lucide-react';

export default function CandidateSetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    cargo: '',
    experiencia: '',
    cnpj_empresa_atual: '',
    localizacao: '',
    telefone: '',
    resumo: '',
    is_pcd: false,
    is_50_plus: false,
    consent_share: true,
    disc_profile: ''
  });

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        cnpj_empresa_atual: form.cnpj_empresa_atual.replace(/\D/g, '') || null
      };
      await candidatesAPI.create(data);
      toast.success('Perfil criado com sucesso!');
      navigate('/candidate');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout menuItems={candidateMenuItems}>
      <div className="max-w-2xl mx-auto" data-testid="candidate-setup">
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-cyan-600" />
            </div>
            <CardTitle className="text-2xl">Seu Perfil Profissional</CardTitle>
            <CardDescription>
              Complete seus dados para começar a se candidatar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Seu nome"
                    required
                    data-testid="candidate-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                    placeholder="(00) 00000-0000"
                    data-testid="candidate-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo Atual/Desejado *</Label>
                <Input
                  id="cargo"
                  value={form.cargo}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                  placeholder="Ex: Desenvolvedor Full Stack"
                  required
                  data-testid="candidate-cargo-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="experiencia">Experiência *</Label>
                  <Select
                    value={form.experiencia}
                    onValueChange={(value) => setForm({ ...form, experiencia: value })}
                  >
                    <SelectTrigger data-testid="candidate-exp-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Junior">Junior (0-2 anos)</SelectItem>
                      <SelectItem value="Pleno">Pleno (2-5 anos)</SelectItem>
                      <SelectItem value="Senior">Sênior (5-10 anos)</SelectItem>
                      <SelectItem value="Especialista">Especialista (10+ anos)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="localizacao">Localização</Label>
                  <Input
                    id="localizacao"
                    value={form.localizacao}
                    onChange={(e) => setForm({ ...form, localizacao: e.target.value })}
                    placeholder="Ex: São Paulo - SP"
                    data-testid="candidate-location-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cnpj_empresa_atual">CNPJ da Empresa Atual (opcional)</Label>
                <Input
                  id="cnpj_empresa_atual"
                  value={form.cnpj_empresa_atual}
                  onChange={(e) => setForm({ ...form, cnpj_empresa_atual: formatCNPJ(e.target.value) })}
                  placeholder="00.000.000/0000-00"
                  data-testid="candidate-cnpj-input"
                />
                <p className="text-xs text-slate-500">
                  Seu empregador atual não poderá ver seu perfil (trava por CNPJ)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="disc_profile">Perfil DISC</Label>
                <Select
                  value={form.disc_profile}
                  onValueChange={(value) => setForm({ ...form, disc_profile: value })}
                >
                  <SelectTrigger data-testid="candidate-disc-select">
                    <SelectValue placeholder="Selecione (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="D - Dominância">D - Dominância</SelectItem>
                    <SelectItem value="I - Influência">I - Influência</SelectItem>
                    <SelectItem value="S - Estabilidade">S - Estabilidade</SelectItem>
                    <SelectItem value="C - Conformidade">C - Conformidade</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resumo">Resumo Profissional</Label>
                <Textarea
                  id="resumo"
                  value={form.resumo}
                  onChange={(e) => setForm({ ...form, resumo: e.target.value })}
                  placeholder="Fale um pouco sobre sua experiência e objetivos..."
                  rows={4}
                  data-testid="candidate-summary-input"
                />
              </div>

              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-medium text-slate-900">Políticas de Inclusão</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Essas informações são usadas apenas para indicar elegibilidade a programas de diversidade
                </p>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_pcd">Pessoa com Deficiência (PCD)</Label>
                  <Switch
                    id="is_pcd"
                    checked={form.is_pcd}
                    onCheckedChange={(checked) => setForm({ ...form, is_pcd: checked })}
                    data-testid="candidate-pcd-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="is_50_plus">Profissional 50+</Label>
                  <Switch
                    id="is_50_plus"
                    checked={form.is_50_plus}
                    onCheckedChange={(checked) => setForm({ ...form, is_50_plus: checked })}
                    data-testid="candidate-50plus-switch"
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                  <div>
                    <Label>Perfil visível para empresas</Label>
                    <p className="text-xs text-slate-500">
                      Ao se cadastrar, seu perfil aparece nas buscas das empresas
                    </p>
                  </div>
                  <span className="text-xs font-medium text-green-700" data-testid="candidate-consent-always">Sempre ativo</span>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                disabled={loading}
                data-testid="candidate-setup-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Criar Perfil'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
